import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../Components/ManageColumns"
import {
  CARD_TYPE_DATA,
  COUNTRY_DATA,
  CURRENCY_DATA,
  FLAG_REASON_DATA,
  ISSUER_BANK_DATA,
  ISSUER_DATA,
  PROCESSOR_DATA,
  REASON_CODE_DATA,
  SUBMISSION_DATA
} from "../DisputesPage/pages/AllPage/constants/allPageData"
import { networkGroupFilterData } from "../DisputesPage/pages/AllPage/constants/categoryGroups"
import { STATUS_DATA, TYPE_DATA } from "../DisputesPage/pages/AllPage/constants/statusMeta"
import UserCreators from "../User/reducer"
import AdminDisputesTable from "./components/AdminDisputesTable/AdminDisputesTable"
import { buildAdminColumns, buildAdminFieldDefs } from "./components/AdminDisputesTable/columns"
import { ADMIN_COLUMN_ICONS, ADMIN_FILTER_FIELDS } from "./constants/columns"
import { ADMIN_FILTER_TYPES } from "./constants/filters"
import { PAID_STATUS_DATA } from "./constants/paidStatus"
import { useAdminDisputesQuery } from "./hooks/useAdminDisputesQuery"
import {
  clearFilters,
  selectAdminDisputesColumns,
  selectAdminDisputesExport,
  selectAdminDisputesFetching,
  selectAdminDisputesFilters,
  selectAdminDisputesList,
  selectAdminDisputesVisibleFilters,
  selectAreFiltersDefault,
  setProp
} from "./redux/adminDisputes.slice"
import {
  getAdminDisputes,
  getAdminDisputesExport,
  pinAdminDispute
} from "./redux/adminDisputes.thunks"

// The table renders the merchant list's own cells, so it needs that page's table styling
// (.disputes-table, .dispute-status-tag, .dispute-tag, .disputes-pin-btn). Every selector
// in there is prefixed, so nothing else on this page is affected.
import "../DisputesPage/styles.less"

const { Title, Text } = Typography

// Internal admin view of every dispute across every business account: the merchant list's
// table, one level up. Same status vocabulary, the same column definitions and the same
// filter chips as /recover/all, so support can read a row here and on the merchant's own
// screen without translating between two designs. On top of those it carries what only an
// admin needs: the account, its owner and URL, the Test On / Test Off switch, and whether
// our fee on a win has been paid.
const AdminDisputesPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const { count, results } = useSelector(selectAdminDisputesList)
  const fetching = useSelector(selectAdminDisputesFetching)
  const filters = useSelector(selectAdminDisputesFilters)
  const visibleFilters = useSelector(selectAdminDisputesVisibleFilters)
  const tableColumns = useSelector(selectAdminDisputesColumns)
  const filtersDefault = useSelector(selectAreFiltersDefault)
  const { fetching: exporting, result: exportResult } = useSelector(selectAdminDisputesExport)
  const testData = useSelector((state) => state.globalFilters?.testData)

  // Bumped by a pin so the list is re-read and re-ordered — a pinned row belongs at the
  // top, and only the backend's own ordering knows where that is.
  const [pinTick, setPinTick] = useState(0)

  const fetchDisputes = useCallback((params) => dispatch(getAdminDisputes(params)), [dispatch])

  const { page, resetPage, handleChangeTable } = useAdminDisputesQuery({
    filters,
    testData,
    pinTick,
    fetchDisputes
  })

  // Assignees are a per-account team, and this list spans accounts: until the endpoint
  // serves an admin-wide member list, the chip offers whoever appears in the loaded rows.
  const assigneeData = useMemo(() => {
    const seen = new Map()
    results.forEach((row) => {
      if (row.assigned_to?.id && !seen.has(row.assigned_to.id)) {
        seen.set(row.assigned_to.id, { id: row.assigned_to.id, name: row.assigned_to.name })
      }
    })
    return [...seen.values()]
  }, [results])

  const filterOptions = useMemo(
    () => ({
      status: STATUS_DATA,
      dispute_type: TYPE_DATA,
      submission: SUBMISSION_DATA,
      flag_reason: FLAG_REASON_DATA,
      reason_category: networkGroupFilterData(t),
      reason_code: REASON_CODE_DATA,
      issuer: ISSUER_DATA,
      issuing_bank: ISSUER_BANK_DATA,
      card_type: CARD_TYPE_DATA,
      currency: CURRENCY_DATA,
      card_country: COUNTRY_DATA,
      processor: PROCESSOR_DATA,
      paid_status: PAID_STATUS_DATA,
      assignee: assigneeData
    }),
    [t, assigneeData]
  )

  // The account's own settings open in a new tab so this list stays where it is; the
  // dashboard opens in the admin user view. Selecting the id in the store mirrors what the
  // Alerts panel does, so the settings page knows the account before it reads the URL.
  const handleSelectAccount = useCallback(
    (row, type) => {
      const id = row?.business_account_id
      if (!id) return

      dispatch(UserCreators.userSetProp("admin_selected_business_account_id", id))
      if (type === "business_account") {
        const localTestingUri = "http://localhost:3000"
        const base =
          window.location.origin === localTestingUri
            ? localTestingUri
            : process.env.REACT_APP_BASE_URI
        window.open(`${base}/business_settings/enrollment?business_account_id=${id}`)
      } else {
        navigate(`/admin/dashboard?business_account_id=${id}`)
      }
    },
    [dispatch, navigate]
  )

  const handlePin = useCallback(
    (row) => {
      dispatch(pinAdminDispute({ id: row.id, pin: !row.pinned }))
      setPinTick((n) => n + 1)
    },
    [dispatch]
  )

  const fieldDefs = useMemo(
    () => buildAdminFieldDefs({ t, onSelectAccount: handleSelectAccount }),
    [t, handleSelectAccount]
  )

  const columns = useMemo(
    () => buildAdminColumns({ t, tableColumns, fieldDefs, onPin: handlePin }),
    [t, tableColumns, fieldDefs, handlePin]
  )

  // `<Filters>` emits the whole chip payload; "changeVisible" is the +Add filter toggle.
  const handleChangeFilter = useCallback(
    (value, type) => {
      if (type === "changeVisible") {
        dispatch(setProp({ key: "visibleFilters", value }))
        return
      }
      dispatch(setProp({ key: "filters", value }))
      resetPage()
    },
    [dispatch, resetPage]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearFilters())
    resetPage()
  }, [dispatch, resetPage])

  const handleChangeColumns = useCallback(
    (value) => dispatch(setProp({ key: "columns", value })),
    [dispatch]
  )

  // A row opens the merchant's own dispute page in the admin user view, so every feature on
  // that page is available rather than reimplemented here.
  const handleRowClick = useCallback(
    (row) => {
      if (row?.business_account_id) {
        dispatch(
          UserCreators.userSetProp("admin_selected_business_account_id", row.business_account_id)
        )
      }
      navigate(`/recover/details/${row.id}?business_account_id=${row.business_account_id}`)
    },
    [dispatch, navigate]
  )

  const handleExport = useCallback(
    (type) => {
      dispatch(getAdminDisputesExport({ export_mode: type === "all" ? "all" : "filtered" }))
    },
    [dispatch]
  )

  useEffect(() => {
    document.title = "Disputes - Chargeback"
  }, [])

  useEffect(() => {
    if (!exportResult) return

    notification.success({
      message: t("Exporting"),
      description: exportResult?.message,
      closeIcon: <Text>{t("Dismiss")}</Text>,
      style: { minWidth: "max-content", width: 475 }
    })
    dispatch(setProp({ key: "exportResult", value: null }))
  }, [exportResult, dispatch, notification, t])

  const TableEmpty = useCallback(
    () =>
      filtersDefault ? (
        <EmptyComponent type="no_disputes" />
      ) : (
        <EmptyComponent type="search">
          <Button className="center" onClick={handleClearFilters} text={t("Clear filters")} />
        </EmptyComponent>
      ),
    [filtersDefault, handleClearFilters, t]
  )

  return (
    <div className="users-page admin-disputes-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {t("Disputes")}
      </Title>

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          filtersTypeMap={ADMIN_FILTER_TYPES}
          allFilters={ADMIN_FILTER_FIELDS}
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          columnIconMap={ADMIN_COLUMN_ICONS}
          newTagStyle="newTagStyle"
          showClearBtn={!filtersDefault}
          onClearFilters={handleClearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData setPageNumber={resetPage} />
          <ExportDropdown loading={exporting} onExport={handleExport} isAdmin disabled={!count} />
          <ManageColumn
            myColumns={tableColumns}
            onChangeColumns={handleChangeColumns}
            iconsType="disputes"
            pinnedColumns={["Status"]}
          />
        </Space>
      </Flex>

      <AdminDisputesTable
        loading={fetching}
        results={results}
        columns={columns}
        count={count}
        page={page}
        onChange={handleChangeTable}
        onRowClick={handleRowClick}
        EmptyComponent={TableEmpty}
      />
    </div>
  )
}

export default AdminDisputesPage
